import { useEffect, useMemo, useRef, useState } from "react";
import { addDoc, collection, doc, DocumentReference, getDoc, getDocs, query, serverTimestamp, Timestamp, updateDoc, where } from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { useNavigate, useParams } from "react-router-dom";
import { auth, db, storage } from "@/firebase";
import SendStatusModal from "@/components/mail/SendStatusModal";

type InboundStatus = "open" | "processing" | "done";

interface MailMessage {
  id: string;
  from?: string;
  fromName?: string;
  subject?: string;
  body?: string;
  bodyHtml?: string;
  createdAt?: Timestamp;
  status?: InboundStatus;
  ownerId?: string | null;
  customerEmail?: string;
  customerId?: string | null;
  customerName?: string | null;
  direction?: string;
  attachments?: Array<{ fileName?: string; storagePath?: string; contentType?: string; size?: number; downloadUrl?: string }>;
  lastUpdatedBy?: string | null;
  lastUpdatedByName?: string | null;
  lastUpdatedAt?: Timestamp | null;
  repliedBy?: string | null;
  repliedByName?: string | null;
  repliedAt?: Timestamp | null;
}

interface AdminUser {
  id: string;
  name: string;
}

interface MailSenderOption {
  id: string;
  name?: string;
  fromAddress?: string;
}

interface MailTemplate {
  id: string;
  title: string;
  subject: string;
  body: string;
  sign?: string;
  sortNo?: number;
}

const STATUS_LABELS: Record<InboundStatus, string> = {
  open: "未処理",
  processing: "対応中",
  done: "処理済み",
};

const STATUS_STYLES: Record<InboundStatus, string> = {
  open: "bg-gray-100 text-gray-600",
  processing: "bg-blue-100 text-blue-700",
  done: "bg-green-100 text-green-700",
};

const formatDateTime = (timestamp?: Timestamp) => {
  if (!timestamp) return "-";
  return timestamp.toDate().toLocaleString("ja-JP", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function MailMessageDetailPage() {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const [message, setMessage] = useState<MailMessage | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [senderOptions, setSenderOptions] = useState<MailSenderOption[]>([]);
  const [templates, setTemplates] = useState<MailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendStatus, setSendStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [preview, setPreview] = useState<{ to: string; subject: string; body: string; customerName: string | null } | null>(null);
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadAttachment = (downloadUrl: string, fileName: string) => {
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = fileName;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.click();
  };
  const [composeForm, setComposeForm] = useState({
    to: "", subject: "", body: "", cc: "", bcc: "",
    senderAccountId: "", sign: "", templateId: "",
  });

  useEffect(() => {
    if (!messageId) { setError("メール情報が取得できません。"); setLoading(false); return; }
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "mail_messages", messageId));
        if (isMounted && snap.exists()) {
          const data = snap.data() as MailMessage;
          setMessage({ id: snap.id, ...data });
          setComposeForm((prev) => ({
            ...prev,
            to: data.customerEmail || data.from || "",
            subject: data.subject ? `Re: ${data.subject.replace(/^Re:\s*/i, "")}` : "",
          }));
        }
      } catch (err) {
        console.error(err);
        if (isMounted) setError("メール詳細の取得に失敗しました。");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [messageId]);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(query(collection(db, "User"), where("role", "==", "admin")));
        setAdminUsers(snap.docs.map((d) => {
          const data = d.data() as { display_name?: string; display_Name?: string; email?: string };
          return { id: d.id, name: data.display_name || data.display_Name || data.email || d.id };
        }));
      } catch (err) { console.error(err); }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(query(collection(db, "mail_accounts"), where("status", "==", "active")));
        setSenderOptions(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<MailSenderOption, "id">) })));
      } catch (err) { console.error(err); }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(query(collection(db, "mail_templates"), where("sortNo", ">=", 0)));
        setTemplates(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<MailTemplate, "id">) }))
          .sort((a, b) => (a.sortNo ?? 0) - (b.sortNo ?? 0)));
      } catch (err) { console.error(err); }
    };
    load();
  }, []);

  const updateMessage = async (updates: Partial<Pick<MailMessage, "status" | "ownerId">>) => {
    if (!messageId) return;
    setStatusUpdating(true);
    try {
      const currentUser = auth.currentUser;
      const currentUserName =
        adminUsers.find((u) => u.id === currentUser?.uid)?.name ||
        currentUser?.displayName ||
        currentUser?.email ||
        null;
      const patch = {
        ...updates,
        lastUpdatedBy: currentUser?.uid ?? null,
        lastUpdatedByName: currentUserName,
        lastUpdatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "mail_messages", messageId), patch);
      setMessage((prev) => prev ? { ...prev, ...updates, lastUpdatedBy: currentUser?.uid ?? null, lastUpdatedByName: currentUserName } : prev);
    } catch (err) { console.error("更新エラー:", err); }
    finally { setStatusUpdating(false); }
  };

  const handleOwnerChange = async (ownerId: string) => {
    await updateMessage({ ownerId: ownerId || null, status: "processing" });
  };

  // 送信ボタン押下 → プレビュー生成
  const handlePreview = async () => {
    const to = composeForm.to.trim();
    const body = composeForm.body.trim();
    if (!to || !body) { setSendError("To / 本文 は必須です。"); return; }

    setSendError(null);
    const customerEmail = message?.customerEmail || to;
    let customerName: string | null = null;
    const userSnap = await getDocs(query(collection(db, "User"), where("email", "==", customerEmail)));
    if (!userSnap.empty) {
      const userData = userSnap.docs[0].data() as { display_name?: string; display_Name?: string };
      customerName = userData.display_name || userData.display_Name || null;
    }

    const replaceVars = (text: string) =>
      text
        .replace(/\{\{user\.display_name\}\}/g, customerName || "")
        .replace(/\{\{user\.name\}\}/g, customerName || "")
        .replace(/\{\{to\}\}/g, to);

    const resolvedBody = replaceVars(body);
    const resolvedSign = composeForm.sign ? replaceVars(composeForm.sign) : "";
    const bodyWithSign = resolvedSign ? `${resolvedBody}\n\n${resolvedSign}` : resolvedBody;

    setPreview({ to, subject: composeForm.subject, body: bodyWithSign, customerName });
  };

  // プレビュー確認後に実際に送信
  const handleSend = async () => {
    if (!preview) return;
    const to = preview.to;

    setSending(true);
    setSendError(null);
    setSendStatus("sending");
    try {
      const customerEmail = message?.customerEmail || to;

      let customerId: string | null = null;
      let customerRef: DocumentReference | null = null;
      let customerName: string | null = preview.customerName;
      const userSnap = await getDocs(query(collection(db, "User"), where("email", "==", customerEmail)));
      if (!userSnap.empty) {
        const userDoc = userSnap.docs[0];
        customerId = userDoc.id;
        customerRef = doc(db, "User", userDoc.id);
      }

      // 添付ファイルをCloud Storageにアップロード
      const tempId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const uploadedAttachments: Array<{ fileName: string; storagePath: string; contentType: string; size: number }> = [];
      for (const file of attachFiles) {
        const safeName = file.name.replace(/[^\w.\-]/g, "_");
        const storagePath = `mail_attachments_out/${tempId}/${safeName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file, { contentType: file.type || "application/octet-stream" });
        uploadedAttachments.push({ fileName: file.name, storagePath, contentType: file.type || "application/octet-stream", size: file.size });
      }

      await addDoc(collection(db, "mail_messages"), {
        direction: "out",
        from: auth.currentUser?.email || "",
        to,
        subject: preview.subject,
        cc: composeForm.cc,
        bcc: composeForm.bcc,
        body: preview.body,
        sign: composeForm.sign,
        customerEmail,
        customerId,
        customerRef,
        customerName,
        staffUserId: auth.currentUser?.uid,
        createdAt: serverTimestamp(),
        replyToMessageId: messageId,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : [],
      });

      // 返信送信 → 処理済みに（返信者情報も記録）
      const currentUser = auth.currentUser;
      const currentUserName =
        adminUsers.find((u) => u.id === currentUser?.uid)?.name ||
        currentUser?.displayName ||
        currentUser?.email ||
        null;
      await updateDoc(doc(db, "mail_messages", messageId!), {
        status: "done",
        repliedBy: currentUser?.uid ?? null,
        repliedByName: currentUserName,
        repliedAt: serverTimestamp(),
        lastUpdatedBy: currentUser?.uid ?? null,
        lastUpdatedByName: currentUserName,
        lastUpdatedAt: serverTimestamp(),
      });
      setMessage((prev) => prev ? { ...prev, status: "done", repliedBy: currentUser?.uid ?? null, repliedByName: currentUserName } : prev);
      setIsComposeOpen(false);
      setPreview(null);
      setAttachFiles([]);
      setComposeForm((prev) => ({ ...prev, body: "" }));
      setSendStatus("success");
    } catch (err) {
      setSendError("送信に失敗しました。");
      setSendStatus("error");
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const bodyText = useMemo(() => message?.body || "", [message]);
  const currentStatus = (message?.status || "open") as InboundStatus;
  const ownerName = adminUsers.find((u) => u.id === message?.ownerId)?.name ?? null;

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!message) return <div className="p-4">メールが見つかりませんでした。</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {sendStatus !== "idle" && (
        <SendStatusModal
          status={sendStatus}
          errorMessage={sendStatus === "error" ? sendError : null}
          onClose={() => {
            if (sendStatus === "success") {
              navigate("/admin/mail");
            } else {
              setSendStatus("idle");
            }
          }}
        />
      )}
      <div className="mx-auto max-w-4xl space-y-4">
        <button type="button" onClick={() => navigate("/admin/mail")} className="text-sm text-blue-600 hover:underline">
          ← メール一覧に戻る
        </button>

        {/* ステータス・担当者 */}
        <div className="rounded-lg border bg-white px-4 py-3 space-y-3 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_STYLES[currentStatus]}`}>
                {STATUS_LABELS[currentStatus]}
              </span>
              {ownerName && <span className="text-xs text-blue-700 font-medium">担当: {ownerName}</span>}
            </div>
            <div className="flex items-center gap-2">
              {(["open", "processing", "done"] as InboundStatus[]).map((s) => (
                <button
                  key={s}
                  onClick={() => updateMessage({ status: s })}
                  disabled={statusUpdating || currentStatus === s}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    currentStatus === s
                      ? `border-transparent ${STATUS_STYLES[s]} font-semibold cursor-default`
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 border-t pt-2 flex-wrap">
            <span className="text-xs text-gray-500 font-medium">担当者：</span>
            <select
              className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none"
              value={message.ownerId || ""}
              onChange={(e) => handleOwnerChange(e.target.value)}
              disabled={statusUpdating}
            >
              <option value="">未割り当て</option>
              {adminUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            {message.lastUpdatedByName && (
              <span className="text-xs text-gray-400 ml-auto">
                最終操作：<span className="text-gray-600 font-medium">{message.lastUpdatedByName}</span>
              </span>
            )}
          </div>
          {message.repliedByName && (
            <div className="flex items-center gap-2 border-t pt-2 text-xs text-gray-400">
              返信担当：<span className="text-gray-600 font-medium">{message.repliedByName}</span>
            </div>
          )}
        </div>

        {/* アクション */}
        <div className="flex gap-2">
          <button
            className="rounded border border-blue-600 bg-blue-600 text-white px-4 py-2 text-sm font-semibold hover:bg-blue-700"
            onClick={async () => {
              setSendError(null);
              setIsComposeOpen(true);
              // 返信ボタン押下 → 未処理なら対応中へ
              if (currentStatus === "open") {
                await updateMessage({ status: "processing" });
              }
            }}
          >
            ✉️ 返信する
          </button>
          <button
            className="rounded border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
            onClick={() => updateMessage({ status: "done" })}
            disabled={statusUpdating || currentStatus === "done"}
          >
            ✓ 処理済みにする
          </button>
        </div>

        {/* メール本文 */}
        <div className="rounded-lg border bg-white shadow-sm">
          <div className="border-b px-4 py-3 space-y-1">
            <div className="text-lg font-semibold text-gray-800">{message.subject || "(件名なし)"}</div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span>
                差出人：<span className="text-gray-700">{message.fromName || message.from || "-"}</span>
                {message.from && message.fromName && <span className="ml-1 text-gray-400">&lt;{message.from}&gt;</span>}
              </span>
              <span>{formatDateTime(message.createdAt)}</span>
            </div>
            {message.customerName && (
              <div className="text-xs">
                顧客：{message.customerId
                  ? <a href={`/admin/users/${message.customerId}`} className="text-blue-600 hover:underline font-medium">{message.customerName}</a>
                  : <span className="text-blue-600">{message.customerName}</span>
                }
              </div>
            )}
          </div>
          <div className="px-4 py-4">
            <div className="rounded border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {bodyText || "本文がありません。"}
            </div>
          </div>
          {message.attachments && message.attachments.length > 0 ? (
            <div className="border-t px-4 py-3">
              <div className="text-xs font-semibold text-gray-500 mb-2">添付ファイル（{message.attachments.length}件）</div>
              <ul className="space-y-2">
                {message.attachments.map((a, i) => (
                  <li key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-gray-400 text-lg">📎</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{a.fileName || "attachment"}</div>
                      {a.size && <div className="text-xs text-gray-400">{(a.size / 1024).toFixed(1)} KB</div>}
                    </div>
                    {a.downloadUrl ? (
                      <button
                        onClick={() => handleDownloadAttachment(a.downloadUrl!, a.fileName || "attachment")}
                        className="shrink-0 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                      >
                        ダウンロード
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>

      {/* 返信モーダル */}
      {isComposeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div className="text-lg font-semibold text-gray-800">✉️ 返信作成</div>
              <button className="text-gray-500 hover:text-gray-700 text-xl" onClick={() => setIsComposeOpen(false)}>×</button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">テンプレート</label>
                <select
                  className="w-full rounded-lg border px-3 py-2"
                  value={composeForm.templateId}
                  onChange={(e) => {
                    const tmpl = templates.find((t) => t.id === e.target.value);
                    setComposeForm((prev) => ({
                      ...prev, templateId: e.target.value,
                      subject: tmpl?.subject || prev.subject,
                      body: tmpl?.body || prev.body,
                      sign: tmpl?.sign || prev.sign,
                    }));
                  }}
                >
                  <option value="">テンプレートを選択</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">To</label>
                <input type="email" className="w-full rounded-lg border px-3 py-2"
                  value={composeForm.to} onChange={(e) => setComposeForm((prev) => ({ ...prev, to: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">件名</label>
                <input type="text" className="w-full rounded-lg border px-3 py-2"
                  value={composeForm.subject} onChange={(e) => setComposeForm((prev) => ({ ...prev, subject: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">本文</label>
                <textarea className="w-full min-h-[180px] rounded-lg border px-3 py-2 resize"
                  value={composeForm.body} onChange={(e) => setComposeForm((prev) => ({ ...prev, body: e.target.value }))}
                  placeholder="本文を入力してください" />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">署名</label>
                <input type="text" className="w-full rounded-lg border px-3 py-2"
                  value={composeForm.sign} onChange={(e) => setComposeForm((prev) => ({ ...prev, sign: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">添付ファイル</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                  >
                    📎 ファイルを選択
                  </button>
                  <span className="text-xs text-gray-400">{attachFiles.length === 0 ? "未選択" : `${attachFiles.length}件選択済み`}</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const newFiles = Array.from(e.target.files || []);
                    setAttachFiles((prev) => [...prev, ...newFiles]);
                    e.target.value = "";
                  }}
                />
                {attachFiles.length > 0 && (
                  <ul className="space-y-1">
                    {attachFiles.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                        <span className="text-gray-400">📎</span>
                        <span className="text-sm text-gray-700 flex-1 truncate">{f.name}</span>
                        <span className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} KB</span>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-red-500 text-xs ml-1"
                          onClick={() => setAttachFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        >✕</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {sendError && <div className="text-sm text-red-600">{sendError}</div>}
              <div className="flex justify-end gap-3 pt-2">
                <button className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsComposeOpen(false)} disabled={sending}>キャンセル</button>
                <button className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  onClick={handlePreview} disabled={sending}>確認 →</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 送信確認プレビューモーダル */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-semibold text-gray-800 text-lg">送信内容の確認</h2>
              <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">宛先</label>
                <input
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={preview.to}
                  onChange={(e) => setPreview((p) => p ? { ...p, to: e.target.value } : p)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">件名</label>
                <input
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={preview.subject}
                  onChange={(e) => setPreview((p) => p ? { ...p, subject: e.target.value } : p)}
                />
              </div>
              {preview.customerName && (
                <div className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
                  顧客名：{preview.customerName}（テンプレート変数置換済み）
                </div>
              )}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">本文（変換後・署名含む）</label>
                <textarea
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm min-h-[240px] resize-y font-mono"
                  value={preview.body}
                  onChange={(e) => setPreview((p) => p ? { ...p, body: e.target.value } : p)}
                />
              </div>
            </div>
            {sendError && <div className="px-5 py-2 text-sm text-red-600">{sendError}</div>}
            <div className="flex justify-end gap-3 px-5 py-4 border-t">
              <button
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setPreview(null)} disabled={sending}
              >
                ← 編集に戻る
              </button>
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 font-semibold"
                onClick={handleSend} disabled={sending}
              >
                {sending ? "送信中..." : "📤 送信する"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
